/* eslint-disable @typescript-eslint/no-explicit-any */
import useChartColors from 'components/Chart/settings/colors';
import { ErrorBoundary } from 'components/ErrorBoundary';
import ErrorComponent from 'components/ErrorComponent';
import { Chart as HighChart, Options } from 'highcharts';
import { HighchartsReactRefObject } from 'highcharts-react-official';
import useSalesforceLogin from 'hooks/useSalesforceLogin';
import CardHeader from 'pages/IndexPage/Report/CardHeader/web';
import React, { Ref, useImperativeHandle, useMemo, useRef } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { IndexItem } from 'store/Index/store';
import Chart from '../Chart';
import { hiddenOptions, setupMainOptions } from './chartOptions';
import useMetricsDownloader from './hooks/useMetricsDownloader';
import './styles.scss';
import { IndexSerie } from './utils';
import Box from '@mui/material/Box';

const CHART_HEIGHT = '568px';
const CHART_PRINT_HEIGHT = '420px';
const CHART_PRINT_WIDTH = '842px';
const CHART_FS_HEIGHT = `${window.innerHeight}px`;
let _CHART: HighChart | undefined;

type ChartType =
  | 'area'
  | 'line'
  | 'spline'
  | 'column'
  | 'bar'
  | 'heatmap'
  | 'scatter'
  | 'histogram';

export type BaseChartRef = {
  main: HighChart | undefined;
  phantom: HighChart | undefined;
};

export interface IndexChartProps {
  title?: string;
  defaultIndex: IndexItem;
  chartType?: ChartType;
  constructorType?: string;
  value?: React.ReactNode;
  date?: string;
  customOptions?: Options;
  // isDisabled?: boolean;
  // disableMetrics?: boolean;
  isLoggedIn?: boolean;
  previewOnly?: boolean;
  pdfOnly?: boolean;
  // embedded?: boolean;
  // rangeMode?: number;
  setRangeMode?: (status: number) => void;
  onLoad: any; // load handler for the phantom chart to be printed
  // onSelectDate: any;
  series: IndexSerie[];
  maxPrintDate?: number; // xMax for the phantom chart
  pointFormatter?: any;
  height?: string;
  compareMode?: string;
  displayLogo?: boolean;
  palette?: string[];
  header?: React.ReactNode;
}

const BaseChartComponent = React.forwardRef(
  (
    {
      defaultIndex,
      title,
      value,
      date,
      series,
      previewOnly,
      pdfOnly,
      onLoad,
      chartType = 'line',
      constructorType = 'stockChart',
      maxPrintDate,
      setRangeMode,
      isLoggedIn = false,
      pointFormatter,
      customOptions,
      height = CHART_HEIGHT,
      compareMode = 'percent',
      displayLogo = false,
      palette = [],
      header,
    }: // disableMetrics = false,
    IndexChartProps,
    ref: Ref<BaseChartRef>
  ) => {
    const handle = useFullScreenHandle();
    const isFullScreen = handle.active;
    const { colors, defaultColor } = useChartColors();
    const isLoaded = series.length > 0;
    const phantomChart = useRef<HighchartsReactRefObject>(undefined);
    const mainChart = useRef<HighchartsReactRefObject>(undefined);
    const { onLogin } = useSalesforceLogin();

    useImperativeHandle(ref, () => ({
      main: mainChart.current?.chart,
      phantom: phantomChart.current?.chart,
    }));

    const metricDownloader = useMetricsDownloader(defaultIndex.plotMetric);
    const buttonColor = defaultColor;

    const roundingDecimals = Number(defaultIndex?.webpage?.indexLevels?.roundingDecimals) || 2;

    // const colorPicker = useColorPicker(selected);

    _CHART = phantomChart.current?.chart;
    // const index = main;

    const onFullScreen = () => {
      if (isFullScreen) {
        handle.exit();
      } else {
        handle.enter();
      }
    };

    const downloadMetrics = () => {
      metricDownloader.onOpen();
    };

    const loginDialog = {
      loginDialog: {
        text: isLoggedIn ? 'Download CSV' : 'Download CSV (login required)',
        onclick: isLoggedIn ? downloadMetrics : onLogin,
      },
    };

    const webSettings = defaultIndex.webpage;
    const hasChart = !webSettings?.indexLevels?.hideChart;
    const chartStyle = hasChart ? { height: '100%' } : { display: 'none' };

    const hiddenHash = series
      // .filter(s => (s as IndexSerie).isIndex)
      .map(s => s.name + ':' + s.metric)
      .join(' ');

    const selectedColors = [...palette, ...colors];
    const chart = (
      <div className="shadow-wrapper" style={{ flex: 1 }}>
        {!pdfOnly && (
          <>
            {metricDownloader.component}
            <div className="syd" style={chartStyle}>
              {useMemo(() => {
                const options = {
                  isFullScreen,
                  onFullScreen,
                  buttonColor,
                  loginDialog,
                  setRangeMode,
                  pointFormatter,
                  custom: customOptions,
                  roundingDecimals,
                };

                return (
                  !!series &&
                  !!series.length && (
                    <Chart
                      ref={mainChart}
                      colors={selectedColors}
                      chartType={chartType}
                      compareMode={compareMode}
                      series={series}
                      constructorType={constructorType}
                      // roundingDecimals={roundingDecimals}
                      height={!isFullScreen ? undefined : CHART_FS_HEIGHT}
                      setupOptions={(defaultOptions: Options) =>
                        setupMainOptions(defaultOptions, options)
                      }
                    />
                  )
                );
              }, [hiddenHash])}
            </div>
          </>
        )}

        <div className="bud" style={{ display: 'none' }}>
          {useMemo(() => {
            return (
              !previewOnly && (
                <Chart
                  ref={phantomChart}
                  colors={colors}
                  chartType={chartType}
                  compareMode={compareMode}
                  series={series}
                  constructorType={constructorType}
                  height={CHART_PRINT_HEIGHT}
                  width={CHART_PRINT_WIDTH}
                  setupOptions={(defaultOptions: Options) =>
                    hiddenOptions(defaultOptions, onLoad, maxPrintDate, customOptions)
                  }
                />
              )
            );
          }, [previewOnly, hiddenHash, maxPrintDate])}
        </div>
      </div>
    );

    // const allOptions = [...options, ...extraOptions];

    if (!isLoaded) {
      return <div>Loading...</div>;
    } else {
      return (
        <ErrorBoundary errorPage={<ErrorComponent />}>
          <FullScreen handle={handle}>
            <Box
              sx={{ height: `calc(${height} - 24px)`, display: 'flex', flexDirection: 'column' }}
            >
              <Box
                className="chart-header"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <CardHeader
                  title={title || 'Index Levels'}
                  value={value}
                  date={date}
                  displayLogo={displayLogo}
                />
              </Box>
              {header}
              {chart}
            </Box>
          </FullScreen>
        </ErrorBoundary>
      );
    }
  }
);

BaseChartComponent.displayName = 'BaseChartComponent';

export default BaseChartComponent;

const beforeChartPrint = async function () {
  return _CHART && _CHART.setSize(760, 540, false);
  // _CHART.reflow();
};

const testPrintAction = function (mql: any) {
  if (mql.matches) {
    beforeChartPrint();
  }
};

const mediaQueryList = window.matchMedia('print');
mediaQueryList.addEventListener('change', testPrintAction);
